import React, { useEffect, useState } from 'react'
import { apiJson } from '../../lib/api'
import { Failed } from '../../components/pending'
import { plural } from '../wages/format'
import o from '../../components/Overlay.module.css'
import jr from './JobRolesEditor.module.css'

// The job roles on offer (D5_APPROVAL_SHEET B, approved: B-1(a) to B-4).
// An ADMIN or SYSADMIN adds and retires the tenant's labels; everyone else
// sees the list. Retiring a role stops it being offered and leaves it on the
// people who hold it (B-2(a)). A rename is a retire and an add (B-3(a)), so
// the people holding the old name keep it until their record is changed.
// Names are unique among the live roles, whatever their case (B-4). Each
// action is saved as it is made.

export interface RoleEntry {
  id: string
  name: string
  created_at: string
  retired_at: string | null
  holders: number
}

type Ask = { kind: 'retire' | 'rename'; role: RoleEntry } | null

export function JobRolesEditor({ user, onDirty }: { user: any; onDirty?: (dirty: boolean) => void }) {
  const [entries, setEntries] = useState<RoleEntry[] | null>(null)
  const [canManage, setCanManage] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [ask, setAsk] = useState<Ask>(null)
  const [rename, setRename] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = async () => {
    try {
      const r = await apiJson<{ entries?: RoleEntry[]; can_manage?: boolean }>(user, '/api/personnel/roles')
      setEntries(Array.isArray(r?.entries) ? r.entries : [])
      setCanManage(!!r?.can_manage)
      setLoadError(null)
    } catch (e: any) {
      setLoadError(e?.message || 'The job roles could not be loaded')
    }
  }
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])
  useEffect(() => {
    onDirty?.(!!draft.trim() || (ask?.kind === 'rename' && !!rename.trim()))
  }, [draft, rename, ask, onDirty])

  const act = async (fn: () => Promise<string>) => {
    if (busy) return
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      setDone(await fn())
      await load()
    } catch (e: any) {
      setError(e?.message || 'Nothing was changed')
    } finally {
      setBusy(false)
    }
  }

  const add = () =>
    act(async () => {
      const name = draft.trim()
      if (!name) throw new Error('Name the role first.')
      const r = await apiJson<{ role: { name: string } }>(user, '/api/personnel/roles', { body: { name } })
      setDraft('')
      return `${r.role?.name || name} added`
    })
  const retire = (role: RoleEntry) =>
    act(async () => {
      await apiJson(user, `/api/personnel/roles/${role.id}/retire`, { body: {} })
      setAsk(null)
      return `${role.name} retired`
    })
  const replace = (role: RoleEntry) =>
    act(async () => {
      const name = rename.trim()
      if (!name) throw new Error('Name the new role first.')
      const r = await apiJson<{ role: { name: string } }>(user, `/api/personnel/roles/${role.id}/replace`, { body: { name } })
      setAsk(null)
      setRename('')
      return `${role.name} retired and ${r.role?.name || name} added`
    })

  const live = (entries || []).filter((e) => !e.retired_at)
  const retired = (entries || []).filter((e) => e.retired_at)
  const keep = (e: RoleEntry, what: string) =>
    e.holders === 1 ? ` The one person who holds ${what} keeps it.` : e.holders ? ` The ${e.holders} people who hold ${what} keep it.` : ''
  const holders = (e: RoleEntry) => (e.holders ? plural(e.holders, 'person holds', 'people hold') + ' it' : 'Nobody holds it')

  return (
    <div className={jr.section} data-testid="job-roles">
      <span className={o.label}>Job roles on offer</span>
      <div className={o.fieldHint}>
        The roles a person’s record can be given.{' '}
        {canManage
          ? 'Retiring one stops it being offered; the people who hold it keep it until their record is changed.'
          : 'Only an ADMIN can change the roles on offer.'}
      </div>
      {loadError && <Failed text={loadError} testId="roles-failed" />}
      {!entries && !loadError && <div className={o.fieldHint}>Loading…</div>}
      {entries && (
        <ul className={jr.list}>
          {live.map((e) => (
            <li key={e.id} className={jr.item}>
              <div className={jr.line}>
                <span className={jr.name}>{e.name}</span>
                <span className={jr.meta}>{holders(e)}</span>
                {canManage && !ask && (
                  <span className={jr.actions}>
                    <button type="button" className={jr.link} disabled={busy} onClick={() => { setAsk({ kind: 'rename', role: e }); setRename(e.name) }}>
                      Rename
                    </button>
                    <button type="button" className={jr.link} disabled={busy} onClick={() => setAsk({ kind: 'retire', role: e })}>
                      Retire
                    </button>
                  </span>
                )}
              </div>
              {ask?.role.id === e.id && ask.kind === 'retire' && (
                <div className={jr.ask} role="alert">
                  <span>
                    Retire {e.name}? It is no longer offered, and its name is not used for a new role.
                    {keep(e, 'it')}
                  </span>
                  <button type="button" className={jr.ghost} disabled={busy} onClick={() => setAsk(null)}>
                    Keep
                  </button>
                  <button type="button" className={jr.primary} disabled={busy} onClick={() => retire(e)}>
                    {busy ? 'Retiring…' : `Retire ${e.name}`}
                  </button>
                </div>
              )}
              {ask?.role.id === e.id && ask.kind === 'rename' && (
                <div className={jr.ask}>
                  <label className={jr.meta} htmlFor={`role-rename-${e.id}`}>
                    New name
                  </label>
                  <input
                    id={`role-rename-${e.id}`}
                    className={`${o.input} ${jr.input}`}
                    value={rename}
                    maxLength={60}
                    onChange={(x) => setRename(x.target.value)}
                    onKeyDown={(x) => {
                      if (x.key === 'Enter') replace(e)
                    }}
                  />
                  <span className={jr.meta}>
                    Renaming retires {e.name} and adds the new name.
                    {keep(e, e.name)}
                  </span>
                  <button type="button" className={jr.ghost} disabled={busy} onClick={() => { setAsk(null); setRename('') }}>
                    Cancel
                  </button>
                  <button type="button" className={jr.primary} disabled={busy || !rename.trim() || rename.trim() === e.name} onClick={() => replace(e)}>
                    {busy ? 'Saving…' : 'Rename'}
                  </button>
                </div>
              )}
            </li>
          ))}
          {!live.length && <li className={jr.meta}>No roles are on offer.</li>}
          {retired.map((e) => (
            <li key={e.id} className={`${jr.item} ${jr.retired}`}>
              <div className={jr.line}>
                <span className={jr.name}>{e.name}</span>
                <span className={jr.chip}>Retired</span>
                <span className={jr.meta}>{holders(e)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {canManage && entries && (
        <div className={jr.addLine}>
          <label className={jr.meta} htmlFor="role-add">
            Add a role
          </label>
          <input
            id="role-add"
            className={`${o.input} ${jr.input}`}
            value={draft}
            maxLength={60}
            placeholder="For example, Forklift driver"
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add()
            }}
          />
          <button type="button" className={jr.ghost} disabled={busy || !draft.trim()} onClick={add}>
            Add
          </button>
        </div>
      )}
      {done && (
        <div className={o.fieldHint} role="status">
          {done}.
        </div>
      )}
      <Failed text={error} testId="roles-failed" />
    </div>
  )
}
